import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export type UserRole = 'admin' | 'printer';

interface User {
  id: string;
  username: string;
  role: UserRole;
  lastLogin?: string;
  eventIds?: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const ADMIN_ONLY_MESSAGE = 'אין הרשאת מנהל';

const clearStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  axios.defaults.baseURL = API_BASE_URL;

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsed: User = JSON.parse(storedUser);
        // Defense in depth: a previously stored non-admin token shouldn't grant access.
        if (parsed.role && parsed.role !== 'admin') {
          clearStorage();
        } else {
          setToken(storedToken);
          setUser(parsed);
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        }
      } catch {
        clearStorage();
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password,
      });

      const { token: newToken, user: newUser } = response.data as { token: string; user: User };

      if (newUser.role !== 'admin') {
        clearStorage();
        throw new Error(ADMIN_ONLY_MESSAGE);
      }

      setToken(newToken);
      setUser(newUser);

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } catch (error: any) {
      if (error?.message === ADMIN_ONLY_MESSAGE) throw error;
      throw new Error(error.response?.data?.message || 'שגיאה בהתחברות');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearStorage();
  };

  const value = {
    user,
    token,
    login,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
